import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { Card } from '@aimsos/ui';

import { resolveExposedColumns } from '../hooks/useWidgetBuilderForm.js';
import { readDisplaySettings } from '../utils/displaySettings.js';
import { readSlotModel } from '../utils/widgetSlotModel.js';
import { WIDGET_FIELD_ROLES } from '../constants/widgetContract.js';
import { isAnswered } from '../utils/slotAssignment.js';
import { TIME_DATA_TYPES } from '../utils/nonFieldBindings.js';
import { dataTypeFits } from '../utils/widgetDataTypes.js';
import BindingSlotsSection from './BindingSlotsSection.jsx';
import InlineNote from './InlineNote.jsx';
import WidgetDisplaySettings from './WidgetDisplaySettings.jsx';
import WidgetIdentityFields from './WidgetIdentityFields.jsx';
import WidgetSettingsSection from './WidgetSettingsSection.jsx';
import WidgetTypePicker from './WidgetTypePicker.jsx';
import { datasetDataMode } from '../utils/widgetTypeAvailability.js';

/**
 * The `Configure` tab (CT-5196 · CT-5197), restructured 2026-09-08.
 *
 * Four blocks, in the order the design source of truth puts them:
 *
 *   1. `NAME YOUR WIDGET` — **first**, at Thomas's request on the 2026-09-08
 *      call: _"lo que sea nombre y descripción a la parte superior ... porque si
 *      yo no tengo el nombre, no me va a dejar guardar"_. It used to sit under
 *      the type picker, which buried the one field that gates the save.
 *   2. `WIDGET TYPE` — the types the Data step's mode can actually produce.
 *      What the chosen one still needs from the Data step is said at the top,
 *      under the name: the grid is 28 tiles tall and a note below it is a note
 *      under the fold.
 *   3. `WHAT EACH COLUMN MEANS` — only for a type that binds named columns, and
 *      only on the record-set path. See `BindingSlotsSection`.
 *   4. `SETTINGS` — the end-user filter toggle and the Metabase escape hatch.
 *
 * Orchestration only; the validity rule lives in `useWidgetBuilderForm`.
 */
function ConfigureTab({ form, widgetTypes, sourceColumns }) {
  const { t } = useTranslation('dashboards');
  const {
    type,
    name,
    description,
    dataset,
    endUserFiltersEnabled,
    slotAssignments,
    nonFieldBindings,
    displaySettings,
    setType,
    setName,
    setDescription,
    setSlotAssignment,
    setNonFieldBinding,
    setDisplaySetting,
    setEndUserFiltersEnabled,
  } = form;

  const selectedType = useMemo(
    () => widgetTypes.types.find((entry) => entry.code === type) ?? null,
    [type, widgetTypes.types],
  );

  /*
    What the type grid gates on. An existing dataset fixes its own shape, so
    it still decides there (CT-5296); the entity path has **no mode to gate
    on** since the selection became a plain record set the widget's bindings
    summarise (CT-5725), and `typeBlockedReason` reads that absence.
  */
  const datasetShape = dataset.sourceType === 'dataset' ? dataset.datasetShape : undefined;
  const dataMode = datasetShape ? datasetDataMode(datasetShape) : null;

  /*
    Every slot the type declares, in schema order, whatever the mode
    (fix/widgets). A measure slot is answered with one of the Data step's
    measures, a dimension slot with one of its groupers and a column slot with
    one of the exposed columns — so a summary has questions too, and the
    optional ones (`KPI.target`, `INDICATOR.min`) finally have somewhere to be
    said. `row_key` is the one slot left out: it has a single candidate.
  */
  const model = useMemo(() => readSlotModel(selectedType?.configSchema), [selectedType]);
  /* The ones the author answers; the record key is shown read-only. */
  const askedSlots = useMemo(
    () => model.slots.filter((slot) => !slot.isRecordKey),
    [model],
  );

  /*
    The type's own look-and-feel options, read from the same `configSchema`
    the slots come from. Unlike the slots this does not depend on the data
    mode: a KPI has a caption and a trend icon whether it summarises or not.
  */
  const displayFields = useMemo(
    () => readDisplaySettings(selectedType?.configSchema).fields,
    [selectedType],
  );

  const exposedColumns = useMemo(
    () => resolveExposedColumns(dataset.exposedColumns),
    [dataset.exposedColumns],
  );

  /*
    The columns the Data step exposed, with their metadata, in the author's
    order (CT-5725). They are what every slot picks from: the dataset is a
    plain record set, so what a slot *does* with a column — the operation,
    the grouping — is the slot's own question, asked below.
  */
  const columnChoices = useMemo(
    () => {
      /*
        A dataset the author picked publishes its own columns and the Data
        step has no picker to narrow them, so there is no `exposedColumns`
        to read (CT-5860). `useSourceColumns` already answers exactly what
        the dataset carries, which is the same list the entity path arrives
        at the long way round.
      */
      if (dataset.sourceType === 'dataset') return sourceColumns.columns;

      return exposedColumns
        .map((fieldUuid) => sourceColumns.columnsByFieldUuid[fieldUuid])
        .filter(Boolean);
    },
    [dataset.sourceType, exposedColumns, sourceColumns.columns, sourceColumns.columnsByFieldUuid],
  );

  /*
    The date columns of the **source**, for the `time` binding.

    Not the pools: `time.field` is declared `x-source: SOURCE_COLUMN` — *"la
    columna DATE/TIMESTAMP de la fuente, no un field de la query"* — so it is
    not limited to what this widget exposes or groups by. Reading it off the
    pools is what left `Period` permanently empty on a `KPI`, which declares
    `supports_grouping: false` and so can never put a date in them.

    The value sent is the column's own code, for the same reason.
  */
  const timeCandidates = useMemo(() => Object.values(sourceColumns.columnsByFieldUuid ?? {})
    .filter((column) => dataTypeFits(column.dataType, TIME_DATA_TYPES))
    .map((column) => ({
      ref: column.fieldUuid,
      code: column.internalName,
      dataType: column.dataType,
      label: column.displayName,
    })), [sourceColumns.columnsByFieldUuid]);

  /*
    A saved period carries the column's code, and the form holds field uuids.
    Resolved here, where the column metadata lives, so reopening a widget
    shows the date field it was saved with.
  */
  const nonFieldValues = useMemo(() => {
    const time = nonFieldBindings.time;
    if (!time?.ref) return nonFieldBindings;
    const match = timeCandidates.find((candidate) => candidate.ref === time.ref
      || candidate.code === time.ref);
    if (!match || match.ref === time.ref) return nonFieldBindings;
    return { ...nonFieldBindings, time: { ...time, ref: match.ref } };
  }, [nonFieldBindings, timeCandidates]);

  // The grouper rule belongs to the type, so it only becomes true here — and
  // here is where the author is standing when they save.
  // The slots the type demands and the author has not answered. Same failure
  // as a missing grouper and it deserves the same warning: without them no
  // contract is written at all, and `publish` answers
  // `DSW012 {missing: bindings}` long after the question went unnoticed —
  // the questions sit below a grid of 28 tiles, which is easy to scroll past.
  const slotGap = useMemo(() => {
    const nameOf = (slot) => t(`widgetBuilder.bindingSlots.names.${slot.key}`, {
      defaultValue: slot.key,
    });
    /*
      Every slot is answered by hand now, so an unanswered required one is
      simply missing: no heuristic will fill it later, and without it the
      contract is not written at all (`DSW012 {missing: bindings}` at
      publish, long after the question went unnoticed).
    */
    const pending = askedSlots.filter((slot) => slot.required && !isAnswered(
      slotAssignments[slot.key],
      { needsAggregate: slot.roles.includes(WIDGET_FIELD_ROLES.AGGREGATE) },
    ));
    if (!pending.length) return null;
    return {
      key: 'widgetBuilder.bindingSlots.missing',
      args: { fields: pending.map(nameOf).join(', ') },
    };
  }, [askedSlots, slotAssignments, t]);

  return (
    <Card className="flex flex-col gap-6 p-5">
      <section className="flex flex-col gap-3">
        <header className="flex flex-col">
          <h3 className="m-0 text-[11px] font-semibold uppercase tracking-wide text-content-secondary">
            {t('widgetBuilder.configure.nameSection')}
          </h3>
          <p className="m-0 text-xs text-content-muted">
            {t('widgetBuilder.configure.nameSectionCaption')}
          </p>
        </header>
        <WidgetIdentityFields
          name={name}
          description={description}
          onNameChange={setName}
          onDescriptionChange={setDescription}
        />
        {/* Above the grid, not under it: what the chosen type still needs is
            the first thing to know on this tab, and at the bottom it sat
            below the fold on a 28-tile grid. */}
        {/* One at a time: a summary has no slots and a record set has no
            groupers, so these never both apply. */}
        {slotGap ? (
          <InlineNote tone="warning">{t(slotGap.key, slotGap.args)}</InlineNote>
        ) : null}
      </section>

      <section className="flex flex-col gap-3">
        <header className="flex flex-col">
          <h3 className="m-0 text-[11px] font-semibold uppercase tracking-wide text-content-secondary">
            {t('widgetBuilder.typePicker.title')}
          </h3>
          <p className="m-0 text-xs text-content-muted">
            {t('widgetBuilder.typePicker.subtitle')}
          </p>
        </header>
        <WidgetTypePicker
          groups={widgetTypes.groups}
          isLoading={widgetTypes.isLoading}
          error={widgetTypes.error}
          dataMode={dataMode}
          datasetShape={datasetShape}
          selectedType={type}
          onSelect={setType}
        />
      </section>

      <BindingSlotsSection
        slots={model.slots}
        nonFieldSlots={model.nonFieldSlots}
        assignments={slotAssignments}
        nonFieldValues={nonFieldValues}
        columns={columnChoices}
        timeCandidates={timeCandidates}
        isLoading={sourceColumns.isLoading}
        onChange={setSlotAssignment}
        onNonFieldChange={setNonFieldBinding}
      />

      {/* The chosen type's own options, before the two every type shares. */}
      <WidgetDisplaySettings
        fields={displayFields}
        values={displaySettings}
        onChange={setDisplaySetting}
      />

      <section className="flex flex-col gap-3">
        <header className="flex flex-col">
          <h3 className="m-0 text-[11px] font-semibold uppercase tracking-wide text-content-secondary">
            {t('widgetBuilder.configure.settingsSection')}
          </h3>
        </header>
        <WidgetSettingsSection
          endUserFiltersEnabled={endUserFiltersEnabled}
          onEndUserFiltersChange={setEndUserFiltersEnabled}
        />
      </section>
    </Card>
  );
}

ConfigureTab.propTypes = {
  form: PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    dataset: PropTypes.shape({
      operationType: PropTypes.string,
      sourceType: PropTypes.string,
      datasetShape: PropTypes.string,
      exposedColumns: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    endUserFiltersEnabled: PropTypes.bool,
    slotAssignments: PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    ).isRequired,
    nonFieldBindings: PropTypes.object.isRequired,
    displaySettings: PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    ).isRequired,
    setType: PropTypes.func.isRequired,
    setName: PropTypes.func.isRequired,
    setDescription: PropTypes.func.isRequired,
    setSlotAssignment: PropTypes.func.isRequired,
    setNonFieldBinding: PropTypes.func.isRequired,
    setDisplaySetting: PropTypes.func.isRequired,
    setEndUserFiltersEnabled: PropTypes.func.isRequired,
  }).isRequired,
  /** The presentation-type catalog — see `useWidgetTypes`. */
  widgetTypes: PropTypes.shape({
    types: PropTypes.arrayOf(PropTypes.object).isRequired,
    groups: PropTypes.arrayOf(PropTypes.object).isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  }).isRequired,
  sourceColumns: PropTypes.shape({
    /** Every column of the source — the whole list a dataset offers. */
    columns: PropTypes.arrayOf(PropTypes.object),
    columnsByFieldUuid: PropTypes.objectOf(PropTypes.object).isRequired,
    isLoading: PropTypes.bool,
  }).isRequired,
};

export default ConfigureTab;
